import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import api from '../lib/api';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import {
    Activity, Users, LogIn, School, Compass, BookOpen,
    RefreshCw, Loader2, ArrowUp, ArrowDown, Minus, CalendarClock, TrendingUp,
} from 'lucide-react';

// ─────────────────────────────────────────────────────────────────────────────
// Configuración de KPIs
// ─────────────────────────────────────────────────────────────────────────────
const METRICS = [
    { key: 'visitas', label: 'Visitas', icon: Activity, color: 'text-blue-600', bg: 'bg-blue-50' },
    { key: 'registros', label: 'Registros', icon: Users, color: 'text-emerald-600', bg: 'bg-emerald-50' },
    { key: 'primera_sesion', label: 'Primera sesión', icon: LogIn, color: 'text-violet-600', bg: 'bg-violet-50' },
    { key: 'establecimientos', label: 'Establecimientos', icon: School, color: 'text-amber-600', bg: 'bg-amber-50' },
    { key: 'estrategias', label: 'Estrategias', icon: Compass, color: 'text-rose-600', bg: 'bg-rose-50' },
    { key: 'modulos', label: 'Uso de módulos', icon: BookOpen, color: 'text-slate-700', bg: 'bg-slate-100' },
];

function deltaInfo(curr, prev) {
    const c = curr || 0;
    const p = prev || 0;
    if (p === 0 && c === 0) return { kind: 'flat', text: '—', pct: null };
    if (p === 0) return { kind: 'up', text: 'nuevo', pct: null };
    const diff = c - p;
    const pct = Math.round((diff / p) * 100);
    if (diff === 0) return { kind: 'flat', text: '0%', pct: 0 };
    return { kind: diff > 0 ? 'up' : 'down', text: `${diff > 0 ? '+' : ''}${pct}%`, pct };
}

function relativeTime(iso) {
    if (!iso) return '';
    const t = new Date(iso).getTime();
    if (Number.isNaN(t)) return '';
    const diff = Date.now() - t;
    const m = Math.round(diff / 60000);
    if (m < 1) return 'hace segundos';
    if (m < 60) return `hace ${m} min`;
    const h = Math.round(m / 60);
    if (h < 24) return `hace ${h} h`;
    const d = Math.round(h / 24);
    if (d < 30) return `hace ${d} d`;
    return new Date(iso).toLocaleDateString('es-CL', { day: '2-digit', month: 'short' });
}

function KpiCard({ metric, value, prev }) {
    const Icon = metric.icon;
    const d = deltaInfo(value, prev);
    return (
        <div
            className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm hover:shadow-md transition-shadow"
            data-testid={`daily-kpi-${metric.key}`}
        >
            <div className="flex items-start justify-between">
                <div className={cn('flex h-9 w-9 items-center justify-center rounded-lg', metric.bg)}>
                    <Icon className={cn('h-4 w-4', metric.color)} />
                </div>
                <span
                    className={cn(
                        'inline-flex items-center gap-0.5 rounded-md px-1.5 py-0.5 text-[10px] font-semibold',
                        d.kind === 'up' && 'bg-emerald-50 text-emerald-700',
                        d.kind === 'down' && 'bg-rose-50 text-rose-700',
                        d.kind === 'flat' && 'bg-slate-100 text-slate-500'
                    )}
                    title={`Ayer: ${prev}`}
                >
                    {d.kind === 'up' && <ArrowUp className="h-3 w-3" />}
                    {d.kind === 'down' && <ArrowDown className="h-3 w-3" />}
                    {d.kind === 'flat' && <Minus className="h-3 w-3" />}
                    {d.text}
                </span>
            </div>
            <p className="mt-3 text-xs text-slate-500">{metric.label}</p>
            <p className="font-mono text-2xl font-bold text-slate-900 leading-tight">{value}</p>
            <p className="mt-0.5 text-[10px] text-slate-400">Ayer: {prev}</p>
        </div>
    );
}

function LineChart({ daily, activeKeys }) {
    // Mini gráfico SVG sin dependencias
    if (!daily?.length) return null;
    const W = 800;
    const H = 200;
    const PAD = { l: 32, r: 12, t: 12, b: 22 };
    const innerW = W - PAD.l - PAD.r;
    const innerH = H - PAD.t - PAD.b;

    const colors = {
        visitas: '#2563eb',
        registros: '#059669',
        primera_sesion: '#7c3aed',
        establecimientos: '#d97706',
        estrategias: '#e11d48',
        modulos: '#475569',
    };

    const maxV = Math.max(
        1,
        ...daily.flatMap((d) => activeKeys.map((k) => d[k] || 0))
    );

    const step = daily.length > 1 ? innerW / (daily.length - 1) : 0;
    const xAt = (i) => PAD.l + step * i;
    const yAt = (v) => PAD.t + innerH - (v / maxV) * innerH;

    // Etiquetas eje X (cada ~5 días)
    const xTicks = daily
        .map((d, i) => ({ ...d, i }))
        .filter((d) => d.i === 0 || d.i === daily.length - 1 || d.i % Math.ceil(daily.length / 6) === 0);

    return (
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-44" data-testid="daily-line-chart">
            {/* Grid horizontal */}
            {[0, 0.25, 0.5, 0.75, 1].map((p) => (
                <line
                    key={p}
                    x1={PAD.l}
                    x2={W - PAD.r}
                    y1={PAD.t + innerH * p}
                    y2={PAD.t + innerH * p}
                    stroke="#e2e8f0"
                    strokeDasharray="2 4"
                />
            ))}
            {/* Eje Y labels */}
            {[1, 0.5, 0].map((p, idx) => (
                <text
                    key={idx}
                    x={PAD.l - 6}
                    y={PAD.t + innerH * p + 3}
                    fill="#94a3b8"
                    fontSize="9"
                    textAnchor="end"
                >
                    {Math.round(maxV * (1 - p))}
                </text>
            ))}
            {/* Líneas por métrica */}
            {activeKeys.map((k) => {
                const points = daily.map((d, i) => `${xAt(i)},${yAt(d[k] || 0)}`).join(' ');
                return (
                    <polyline
                        key={k}
                        fill="none"
                        stroke={colors[k]}
                        strokeWidth="2"
                        strokeLinejoin="round"
                        strokeLinecap="round"
                        points={points}
                    />
                );
            })}
            {/* X labels */}
            {xTicks.map((d) => (
                <text
                    key={d.date}
                    x={xAt(d.i)}
                    y={H - 6}
                    fill="#94a3b8"
                    fontSize="9"
                    textAnchor="middle"
                >
                    {d.date.slice(5)}
                </text>
            ))}
        </svg>
    );
}

export default function DailyOpsPanel() {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [activeKeys, setActiveKeys] = useState(['visitas', 'registros', 'primera_sesion']);

    const fetchData = useCallback(async () => {
        try {
            const { data } = await api.get('/admin/analytics/daily-ops?days=30');
            setData(data);
        } catch {
            toast.error('No se pudieron cargar las métricas diarias');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        setLoading(true);
        fetchData();
    }, [fetchData]);

    const toggleKey = (k) => {
        setActiveKeys((prev) => (prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]));
    };

    const last7 = useMemo(() => {
        if (!data?.conversions) return [];
        return data.conversions.slice(-7).reverse();
    }, [data]);

    if (loading) {
        return (
            <Card data-testid="daily-ops-panel" id="metricas-diarias">
                <CardHeader>
                    <CardTitle className="font-heading text-lg flex items-center gap-2">
                        <CalendarClock className="h-5 w-5" />
                        Métricas Operativas Diarias
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="flex justify-center py-12">
                        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
                    </div>
                </CardContent>
            </Card>
        );
    }

    if (!data) return null;

    const todayLabel = new Date(`${data.today_date}T12:00:00`).toLocaleDateString('es-CL', {
        weekday: 'long',
        day: '2-digit',
        month: 'long',
    });

    return (
        <Card data-testid="daily-ops-panel" id="metricas-diarias">
            <CardHeader>
                <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                        <CardTitle className="font-heading text-lg flex items-center gap-2">
                            <CalendarClock className="h-5 w-5" />
                            Métricas Operativas Diarias
                        </CardTitle>
                        <CardDescription>
                            Hora Chile · Hoy: <strong className="capitalize">{todayLabel}</strong> · Comparado vs. ayer
                        </CardDescription>
                    </div>
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={fetchData}
                        disabled={loading}
                        className="gap-1.5 self-start"
                        data-testid="daily-ops-refresh"
                    >
                        <RefreshCw className={cn('h-3.5 w-3.5', loading && 'animate-spin')} />
                        Refrescar
                    </Button>
                </div>
            </CardHeader>
            <CardContent className="space-y-6">
                {/* KPIs del día */}
                <div className="grid gap-3 grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
                    {METRICS.map((m) => (
                        <KpiCard
                            key={m.key}
                            metric={m}
                            value={data.today[m.key] || 0}
                            prev={data.yesterday[m.key] || 0}
                        />
                    ))}
                </div>

                {/* Gráfico de 30 días */}
                <div className="rounded-xl border border-slate-200 bg-white p-4">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div>
                            <p className="font-heading text-sm font-bold text-slate-900 flex items-center gap-1.5">
                                <TrendingUp className="h-4 w-4" />
                                Histórico últimos {data.window_days} días
                            </p>
                            <p className="text-xs text-slate-500 mt-0.5">
                                Selecciona qué métricas comparar en el gráfico.
                            </p>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                            {METRICS.map((m) => {
                                const on = activeKeys.includes(m.key);
                                return (
                                    <button
                                        key={m.key}
                                        type="button"
                                        onClick={() => toggleKey(m.key)}
                                        data-testid={`daily-toggle-${m.key}`}
                                        className={cn(
                                            'inline-flex items-center gap-1 rounded-full px-2 py-1 text-[10px] font-medium border transition-colors',
                                            on
                                                ? 'bg-slate-900 text-white border-slate-900'
                                                : 'bg-white text-slate-500 border-slate-200 hover:bg-slate-50'
                                        )}
                                    >
                                        <span
                                            className="h-1.5 w-1.5 rounded-full"
                                            style={{
                                                background: {
                                                    visitas: '#2563eb',
                                                    registros: '#059669',
                                                    primera_sesion: '#7c3aed',
                                                    establecimientos: '#d97706',
                                                    estrategias: '#e11d48',
                                                    modulos: '#475569',
                                                }[m.key],
                                            }}
                                        />
                                        {m.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                    <div className="mt-3">
                        <LineChart daily={data.daily} activeKeys={activeKeys} />
                    </div>
                </div>

                <div className="grid gap-6 lg:grid-cols-2">
                    {/* Conversiones últimos 7 días */}
                    <div className="rounded-xl border border-slate-200 bg-white p-4">
                        <p className="font-heading text-sm font-bold text-slate-900">
                            Conversión diaria (últimos 7 días)
                        </p>
                        <p className="text-xs text-slate-500 mt-0.5 mb-3">
                            Porcentaje de visitas que se convierten y luego activan primera sesión.
                        </p>
                        <div className="overflow-x-auto">
                            <table className="w-full text-xs" data-testid="daily-conversions-table">
                                <thead>
                                    <tr className="border-b border-slate-200 text-left">
                                        <th className="pb-2 font-semibold text-slate-500">Día</th>
                                        <th className="pb-2 font-semibold text-slate-500 text-right">Visitas</th>
                                        <th className="pb-2 font-semibold text-slate-500 text-right">→ Reg.</th>
                                        <th className="pb-2 font-semibold text-slate-500 text-right">→ 1ra ses.</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {last7.map((c) => {
                                        const day = data.daily.find((d) => d.date === c.date) || {};
                                        return (
                                            <tr key={c.date} className="border-b border-slate-100 last:border-0">
                                                <td className="py-2 text-slate-700 font-medium">{c.date.slice(5)}</td>
                                                <td className="py-2 text-right font-mono text-slate-700">{day.visitas || 0}</td>
                                                <td className="py-2 text-right font-mono">
                                                    {c.conv_visita_registro != null ? (
                                                        <span className="text-emerald-700">{c.conv_visita_registro}%</span>
                                                    ) : (
                                                        <span className="text-slate-300">—</span>
                                                    )}
                                                </td>
                                                <td className="py-2 text-right font-mono">
                                                    {c.conv_registro_primera_sesion != null ? (
                                                        <span className="text-violet-700">{c.conv_registro_primera_sesion}%</span>
                                                    ) : (
                                                        <span className="text-slate-300">—</span>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Últimos usuarios */}
                    <div className="rounded-xl border border-slate-200 bg-white p-4">
                        <p className="font-heading text-sm font-bold text-slate-900">
                            Últimos usuarios registrados
                        </p>
                        <p className="text-xs text-slate-500 mt-0.5 mb-3">
                            Top 15 más recientes — útil para validar campañas en curso.
                        </p>
                        <div className="space-y-2 max-h-72 overflow-y-auto" data-testid="daily-recent-users">
                            {data.recent_users.length === 0 ? (
                                <p className="text-xs text-slate-400 text-center py-6">Sin registros aún</p>
                            ) : (
                                data.recent_users.map((u) => (
                                    <div
                                        key={u.id}
                                        className="flex items-start justify-between gap-2 py-1.5 border-b border-slate-100 last:border-0"
                                    >
                                        <div className="min-w-0 flex-1">
                                            <p className="text-xs font-medium text-slate-900 truncate">
                                                {u.nombre || u.email}
                                            </p>
                                            <p className="text-[10px] text-slate-500 truncate">{u.email}</p>
                                        </div>
                                        <div className="text-right shrink-0 flex flex-col items-end gap-0.5">
                                            <span className="text-[10px] text-slate-400">{relativeTime(u.created_at)}</span>
                                            <div className="flex gap-1">
                                                {u.auth_provider === 'google' && (
                                                    <Badge className="bg-blue-50 text-blue-700 text-[9px] px-1 py-0">G</Badge>
                                                )}
                                                {u.perfil_completado && (
                                                    <Badge className="bg-emerald-50 text-emerald-700 text-[9px] px-1 py-0">
                                                        ✓
                                                    </Badge>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}
